import React, { useCallback, useEffect, useState } from "react";
import { usePageContent } from "customProviders/LocationProvider";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import {
  Drawer,
  DrawerContent,
  DrawerOverlay,
  DrawerPortal
} from "@RHCommerceDev/unstyled-ui-components";
import {
  PrimaryColumn,
  SecondaryColumn,
  TertiaryColumn
} from "./HamburgerDropdownColumns";
import parsedCatalogNavigation, {
  groupChildCategories
} from "@RHCommerceDev/utils/parseCatalogNavigationData";
import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import BrandLogo from "../UtilityBar/BrandLogo";
import RHCloseIcon from "icons/RHCloseIcon";
import { usePreloadImages } from "@RHCommerceDev/hooks/usePreloadImages";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import memoize from "@RHCommerceDev/utils/memoize";
import getAbsoluteUrl from "@RHCommerceDev/utils/getAbsoluteUrl";
import { CATALOG_CATEGORIES } from "@RHCommerceDev/utils/constants";
import { Title } from "@radix-ui/react-dialog";
import { cn } from "utils/cn";
import { useCookies } from "react-cookie";
import {
  CG_PREVIEW_ID_COOKIE,
  PG_PREVIEW_ID_COOKIE,
  PP_PREVIEW_ID_COOKIE
} from "pages/NextgenProductGallery/constants";

interface SelectedItem {
  sectionIndex: number;
  primaryIndex: number;
  secondaryIndex: number;
}

const DEFAULT_SELECTED_ITEM: SelectedItem = {
  sectionIndex: -1,
  primaryIndex: -1,
  secondaryIndex: -1
};

const MobileHamburgerDrawer = () => {
  const locale = useLocalization();
  const env = useEnv();

  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    false
  );

  const [, , removeCookie] = useCookies([
    PG_PREVIEW_ID_COOKIE,
    PP_PREVIEW_ID_COOKIE,
    CG_PREVIEW_ID_COOKIE
  ]);

  const { pageContent: pageContentObj } = usePageContent();
  const pageContent = React.useMemo(() => {
    return (
      ((pageContentObj?.hamburgerNav as Record<string, unknown>)
        ?.childCategories as Record<string, unknown>[]) || []
    );
  }, [pageContentObj]);

  const sections = React.useMemo(() => {
    const combinedSections = [navigationData];
    combinedSections.push(...pageContent);
    return combinedSections;
  }, [navigationData, pageContent]);

  const { country: selectedCountry, language: selectedLanguage } =
    useUserPreferences();

  const [selectedItem, setSelectedItem] = useState<SelectedItem>(
    DEFAULT_SELECTED_ITEM
  );

  const [isCountrySelectorOpen, setIsCountrySelectorOpen] =
    useState<boolean>(false);

  const { state, closeHamburgerDropdown } = useHeaderAtom();

  const selectedSection = sections?.[selectedItem.sectionIndex];
  const selectedPrimaryItem =
    selectedSection?.childCategories?.[selectedItem.primaryIndex];
  const selectedSecondaryItem =
    selectedPrimaryItem?.childCategories?.[selectedItem.secondaryIndex];

  const groupedCategories = groupChildCategories(
    selectedSecondaryItem?.childCategories
  );

  const clearPreviewCookies = useCallback(() => {
    const previewCookies: any[] = [
      PG_PREVIEW_ID_COOKIE,
      PP_PREVIEW_ID_COOKIE,
      CG_PREVIEW_ID_COOKIE
    ];

    previewCookies.forEach(cookieName => {
      removeCookie(cookieName, { path: "/", domain: env.ENV_DOMAIN });
    });
  }, [removeCookie, env.ENV_DOMAIN]);

  //handle Primary Column button click
  const handlePrimaryItemClick = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      sectionIndex: number,
      primaryIndex: number
    ) => {
      const section = sections?.[sectionIndex];
      const primaryItem = section?.childCategories?.[primaryIndex];

      const isLink = !primaryItem?.childCategories?.length;

      if (!isLink) {
        setIsCountrySelectorOpen(false);
        setSelectedItem({ sectionIndex, primaryIndex, secondaryIndex: -1 });
      } else {
        //analytics
        const displayName = primaryItem?.displayName;
        const target = event?.target as HTMLElement;
        const { id, className } = target;

        document?.body?.dispatchEvent(
          new CustomEvent("cta_click", {
            detail: {
              item: {
                fromHamburger: false,
                isHeaderNavigation: true,
                topCat:
                  sectionIndex > 3
                    ? "footer"
                    : sectionIndex === 0
                    ? displayName
                    : section?.displayName,
                parentCat:
                  sectionIndex > 3
                    ? section?.displayName
                    : displayName?.replaceAll("RH", "").trim(),
                cat: sectionIndex > 3 ? displayName : primaryItem?.targetUrl,
                catURL: primaryItem?.targetUrl,
                class: className,
                id: id
              }
            }
          })
        );

        //handle link click
        if (primaryItem?.linkType === "none") {
          window.open(primaryItem?.targetUrl ?? "", "_blank");
        } else {
          const url = getAbsoluteUrl(primaryItem?.targetUrl ?? "", locale);
          window.location.assign(url);
        }

        //close dropdown after navigating
        closeHamburgerDropdown();
      }
    },
    [sections, locale, closeHamburgerDropdown]
  );

  //handle Secondary Column button Click
  const handleSecondaryItemClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, secondaryIndex: number) => {
      const secondaryItem =
        selectedPrimaryItem?.childCategories?.[secondaryIndex];

      const isLink = !secondaryItem?.childCategories?.length;

      if (!isLink) {
        setSelectedItem(prev => ({ ...prev, secondaryIndex }));
      } else {
        //analytics
        const target = event?.target as HTMLElement;
        const { id, className } = target;
        document?.body?.dispatchEvent(
          new CustomEvent("cta_click", {
            detail: {
              item: {
                fromHamburger: false,
                isHeaderNavigation: true,
                topCat: selectedPrimaryItem?.displayName,
                parentCat: secondaryItem?.displayName,
                cat: secondaryItem?.targetUrl,
                catURL: secondaryItem?.targetUrl,
                class: className,
                id: id
              }
            }
          })
        );

        //handle link click
        const isCatalogSection = selectedItem.sectionIndex === 0;
        const linkType = secondaryItem?.linkType;
        const targetUrl = secondaryItem?.targetUrl ?? "";

        if (isCatalogSection) {
          clearPreviewCookies();

          const isSaleCategory =
            selectedPrimaryItem?.displayName?.toLowerCase() ===
            CATALOG_CATEGORIES.SALE;

          if (isSaleCategory && linkType === "anchor") {
            const formattedUrl = targetUrl.startsWith("https://")
              ? targetUrl
              : `https://${targetUrl}`;
            window.open(formattedUrl, "_blank");
          } else {
            window.location.assign(getAbsoluteUrl(targetUrl, locale));
          }
        } else {
          if (linkType === "none") {
            window.open(targetUrl, "_blank");
          } else {
            window.location.assign(getAbsoluteUrl(targetUrl, locale));
          }
        }

        //close dropdown after navigating
        closeHamburgerDropdown();
      }
    },
    [
      selectedItem,
      selectedPrimaryItem,
      locale,
      closeHamburgerDropdown,
      clearPreviewCookies
    ]
  );

  //handle Tertiary Column button Click
  const handleTertiaryItemClick = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      groupIndex: number,
      tertiaryIndex: number
    ) => {
      const group = groupedCategories?.[groupIndex];
      const groupHeader = group?.header;
      const tertiaryItem = group?.children?.[tertiaryIndex];

      //analytics
      const target = event?.target as HTMLElement;
      const { id, className } = target;
      document?.body?.dispatchEvent(
        new CustomEvent("cta_click", {
          detail: {
            item: {
              fromHamburger: false,
              isHeaderNavigation: true,
              topCat: selectedPrimaryItem?.displayName,
              parentCat: selectedSecondaryItem?.displayName,
              cat: groupHeader
                ? groupHeader?.displayName
                : tertiaryItem?.displayName,
              catURL: groupHeader
                ? tertiaryItem?.displayName
                : tertiaryItem?.targetUrl,
              class: className,
              id: id
            }
          }
        })
      );

      const isCatalogSection = selectedItem.sectionIndex === 0;
      const linkType = tertiaryItem?.linkType;
      const targetUrl = tertiaryItem?.targetUrl ?? "";

      if (isCatalogSection) {
        clearPreviewCookies();
        const isSaleCategory =
          selectedPrimaryItem?.displayName?.toLowerCase() ===
          CATALOG_CATEGORIES.SALE;

        if (isSaleCategory && linkType === "anchor") {
          const formattedUrl = targetUrl.startsWith("https://")
            ? targetUrl
            : `https://${targetUrl}`;
          window.open(formattedUrl, "_blank");
        } else {
          window.location.assign(getAbsoluteUrl(targetUrl, locale));
        }
      } else {
        if (linkType === "none") {
          window.open(targetUrl, "_blank");
        } else {
          window.location.assign(getAbsoluteUrl(targetUrl, locale));
        }
      }

      //close dropdown after navigating
      closeHamburgerDropdown();
    },
    [
      locale,
      selectedItem,
      groupedCategories,
      selectedPrimaryItem,
      selectedSecondaryItem,
      closeHamburgerDropdown,
      clearPreviewCookies
    ]
  );

  //handle country selector click in primary column
  const handleCountrySelectorClick = useCallback(() => {
    setIsCountrySelectorOpen(prev => !prev);
    setSelectedItem(DEFAULT_SELECTED_ITEM);
  }, []);

  //handle back functionality from secondary drawer to primary
  const handleSecondaryBackButtonClick = useCallback(() => {
    setIsCountrySelectorOpen(false);
    setSelectedItem(DEFAULT_SELECTED_ITEM);
  }, []);

  // handle back functionality from tertiary drawer to secondary
  const handleTertiaryBackButtonClick = useCallback(() => {
    setSelectedItem(prev => ({
      ...prev,
      secondaryIndex: -1
    }));
  }, []);

  const imgPublicUrl = (env.PUBLIC_URL || "").replace(new RegExp(/\/?$/gm), "");

  //reset all drawer states
  useEffect(() => {
    if (!state.isHamburgerDropdownOpen) {
      setSelectedItem(DEFAULT_SELECTED_ITEM);
      setIsCountrySelectorOpen(false);
    }
  }, [state.isHamburgerDropdownOpen]);

  // Preload Waterworks logo image
  usePreloadImages(["/img/WaterworksLogoWhite.svg"]);

  const DrawerHeader = () => {
    return (
      <Title asChild>
        <div
          className={cn(
            "relative flex justify-center",
            "pt-3.5 sm:pt-[23px]",
            "h-[82px] shrink-0 sm:h-[102px]", // `shrink-0` helps to apply height
            "bg-rh-mocha text-white"
          )}
        >
          <button
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2",
              "h-[22px] w-[22px] sm:h-[26px] sm:w-[26px]",
              "p-0",
              "text-inherit"
            )}
            onClick={() => {
              closeHamburgerDropdown();
            }}
          >
            <RHCloseIcon className="mx-auto my-auto text-inherit" />
          </button>
          <BrandLogo />
        </div>
      </Title>
    );
  };

  return (
    <>
      {/* Primary Column Drawer */}
      <Drawer open={state.isHamburgerDropdownOpen}>
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent
            direction="left"
            className="w-full bg-rh-mocha px-4 text-white sm:px-8"
          >
            <DrawerHeader />
            <PrimaryColumn
              sections={sections}
              selectedItem={selectedItem}
              onPrimaryItemClick={handlePrimaryItemClick}
              onCountrySelectorClick={handleCountrySelectorClick}
              selectedCountry={selectedCountry}
              selectedLanguage={selectedLanguage.split("-")[0].toUpperCase()}
              isCountrySelectorOpen={isCountrySelectorOpen}
            />
          </DrawerContent>
        </DrawerPortal>
      </Drawer>

      {/* Secondary Column Drawer */}
      <Drawer open={selectedItem.primaryIndex !== -1 || isCountrySelectorOpen}>
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent
            direction="left"
            className="w-full bg-rh-mocha  px-4 text-white sm:px-8"
          >
            <DrawerHeader />
            <SecondaryColumn
              sections={sections}
              selectedItem={selectedItem}
              onSecondaryItemClick={handleSecondaryItemClick}
              onSecondaryBackButtonClick={handleSecondaryBackButtonClick}
              selectedCountry={selectedCountry}
              selectedLanguage={selectedLanguage}
              isCountrySelectorOpen={isCountrySelectorOpen}
              variant="mobile"
              waterWorksUrl={`${imgPublicUrl}/img/WaterworksLogoWhite.svg`}
            />
          </DrawerContent>
        </DrawerPortal>
      </Drawer>

      {/* Tertiary Column Drawer */}
      <Drawer open={selectedItem.secondaryIndex !== -1}>
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent
            direction="left"
            className="w-full bg-rh-mocha  px-4 text-white sm:px-8"
          >
            <DrawerHeader />
            <TertiaryColumn
              sections={sections}
              selectedItem={selectedItem}
              groups={groupedCategories}
              onTertiaryItemClick={handleTertiaryItemClick}
              onTertiaryBackButtonClick={handleTertiaryBackButtonClick}
              variant="mobile"
              waterWorksUrl={`${imgPublicUrl}/img/WaterworksLogoWhite.svg`}
            />
          </DrawerContent>
        </DrawerPortal>
      </Drawer>
    </>
  );
};

export const MemoizedMobileHamburgerDrawer = memoize(MobileHamburgerDrawer);
